import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Clock, Camera, ChevronRight, User, Layout, CheckCircle2, RefreshCw, PenLine, X } from 'lucide-react';
import Swal from 'sweetalert2';
import TomSelect from 'tom-select';
import { OB_TASKS } from '../db/obTasks';

const ObConfirmationList = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extraTasksRefresh, setExtraTasksRefresh] = useState(0);
  const [serverExtraTasks, setServerExtraTasks] = useState([]);

  // State untuk konfirmasi per-item dengan catatan + TTD
  const [confirmPanel, setConfirmPanel] = useState(null); // { report }
  const [catatanKonfirmasi, setCatatanKonfirmasi] = useState('');
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const ctxRef = useRef(null);

  // Setup canvas TTD
  const setupCanvas = useCallback(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { width } = canvas.getBoundingClientRect();
    canvas.width = width * window.devicePixelRatio;
    canvas.height = 150 * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2.5;
    ctxRef.current = ctx;
  }, []);

  useEffect(() => {
    if (confirmPanel) {
      setCatatanKonfirmasi('');
      setTimeout(() => setupCanvas(), 50);
    }
  }, [confirmPanel, setupCanvas]);

  const startDraw = (e, isTouch = false) => {
    isDrawing.current = true;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(x, y);
  };

  const onDraw = (e, isTouch = false) => {
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    ctxRef.current.lineTo(x, y);
    ctxRef.current.stroke();
  };

  const stopDraw = () => { isDrawing.current = false; };

  const clearCanvas = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const mergedTasks = useMemo(() => {
    return OB_TASKS.map(shift => ({
      ...shift,
      tasks: shift.tasks.map(task => {
        // Ambil sub-tugas statis (template)
        const staticSubTasks = task.subTasks || [];

        // Ambil item dari server untuk task_id ini
        const itemsFromServer = serverExtraTasks
          .filter(et => parseInt(et.task_id) === parseInt(task.id));

        // Gabungkan berdasarkan ID
        const finalSubTasks = [...staticSubTasks];
        itemsFromServer.forEach(serverItem => {
          if (!finalSubTasks.find(s => parseInt(s.id) === parseInt(serverItem.id))) {
            finalSubTasks.push(serverItem);
          }
        });

        return {
          ...task,
          subTasks: finalSubTasks
        };
      })
    }));
  }, [serverExtraTasks]);

  const getEmployeeName = (id) => {
    if (!id) return 'Unknown User';

    try {
      const list = JSON.parse(localStorage.getItem('karyawan_pengganti_list') || '[]');
      // Cari yang ID nya cocok
      const emp = list.find(e => String(e.id) === String(id));

      console.log(`[OB Debug HC] Searching for ID ${id}. Found:`, emp ? emp.nama : 'NOT FOUND');

      if (emp) return emp.nama;
    } catch (e) {
      console.error('[OB Debug HC] Error reading employee list:', e);
    }

    return `User ID: ${id}`;
  };

  const fetchExtraTasks = async () => {
    try {
      const res = await fetch('https://jne-cilacap.com/slip/public/api/ob/tasks');
      const json = await res.json();
      if (json.status === 'success') {
        // Filter hanya tugas tambahan yang dibuat HARI INI
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        const extras = [];
        json.data.forEach(task => {
          if (task.items) {
            task.items.forEach(item => {
              // Skip item dari hari sebelumnya
              if (item.updated_at && !item.updated_at.startsWith(todayStr)) return;
              // Resolve nama penambah dari jabatan
              const jabatanPenambah = (item.added_by_jabatan || '').toLowerCase();
              let added_by_name = null;
              if (jabatanPenambah.includes('general affair')) added_by_name = 'Retno Adi Nugroho';
              else if (jabatanPenambah.includes('human capital')) added_by_name = 'Kamalia Nur Fajriyah';

              extras.push({
                task_id: task.id,
                title: item.item_name,
                updated_at: item.updated_at,
                id: item.id, // Ambil ID asli database
                added_by_jabatan: item.added_by_jabatan || null,
                added_by_name
              });
            });
          }
        });
        setServerExtraTasks(extras);

        // Update badge_ob untuk Admin
        const pendingReports = parseInt(localStorage.getItem('badge_ob_pending_reports') || '0');
        localStorage.setItem('badge_ob', pendingReports);
        window.dispatchEvent(new Event('badgeUpdate'));
      }
    } catch (e) {
      console.error('[OB Debug HC] Error fetching extra tasks:', e);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      console.log('[OB Debug HC] Fetching reports from server...');
      const res = await fetch('https://jne-cilacap.com/slip/public/api/ob/reports');
      const json = await res.json();

      if (json.status === 'success') {
        console.log('[OB Debug HC] Reports received:', json.data);

        // Base URL untuk gambar di server Laravel
        const baseUrl = 'https://jne-cilacap.com/slip/images';

        // Map nama dan perbaiki URL gambar jika relatif
        // Filter hanya data hari ini
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        const mappedData = json.data
          .filter(r => r.updated_at && r.updated_at.startsWith(todayStr))
          .map(r => {
            let photoUrl = r.photo_path;
            if (photoUrl && photoUrl.startsWith('/')) {
              photoUrl = `${baseUrl}${photoUrl}`;
            }

            const name = r.user_name || getEmployeeName(r.user_id);
            return {
              ...r,
              photo_path: photoUrl,
              display_name: name
            };
          });
        setReports(mappedData);
        
        // Hitung HANYA laporan PENDING (belum dikonfirmasi) untuk badge
        const pendingReportsOnly = mappedData.filter(r => r.status !== 'Dikonfirmasi');
        localStorage.setItem('badge_ob_pending_reports', pendingReportsOnly.length);
        // Recalculate total badge by fetching extra tasks
        fetchExtraTasks();
      }
    } catch (error) {
      console.error('Error fetching OB reports:', error);
      // Fallback dummy data dengan mapping nama
      setReports([
        { id: 1, task_id: 1, user_id: 1, subtask_index: 0, photo_path: 'https://images.unsplash.com/photo-1581578731522-9b7d7b89751f?w=400', display_name: getEmployeeName(1), status: 'Selesai', updated_at: new Date() },
        { id: 2, task_id: 1, user_id: 1, subtask_index: 1, photo_path: 'https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?w=400', display_name: getEmployeeName(1), status: 'Selesai', updated_at: new Date() }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchExtraTasks();

    // Refresh extra tasks every 30 seconds
    const interval = setInterval(fetchExtraTasks, 30000);
    return () => clearInterval(interval);
  }, []);

  // Mengelompokkan progress berdasarkan Shift dan Task ID
  // Hitung dengan: unique laporan per subtask, include tugas tambahan di denominator,
  // hitung juga confirmed-without-notes dan cap presentase ke 100%
  const shiftProgress = useMemo(() => {
    return mergedTasks.map(shiftData => {
      const shiftTasks = shiftData.tasks.map(task => {
        const isExtraTask = parseInt(task.id) === 10 || parseInt(task.id) === 11;

        // Ambil semua laporan untuk task ini
        const taskReportsAll = reports.filter(r => parseInt(r.task_id) === parseInt(task.id));

        // Pending (belum dikonfirmasi) dan confirmed
        const pendingReports = taskReportsAll.filter(r => r.status !== 'Dikonfirmasi');
        const confirmedReports = taskReportsAll.filter(r => r.status === 'Dikonfirmasi');

        // Hitung unique subtask_index untuk menghindari double-counting (server dupes)
        const uniquePendingCount = new Set(pendingReports.map(r => String(r.subtask_index))).size;
        const uniqueConfirmedCount = new Set(confirmedReports.map(r => String(r.subtask_index))).size;

        // Confirmed without notes (unique)
        const confirmedNoNotesCount = new Set(
          confirmedReports.filter(r => !r.catatan || r.catatan.trim() === '').map(r => String(r.subtask_index))
        ).size;

        const totalSubTasks = (task.subTasks || []).length;

        // For extra tasks (id 10/11) treat their subtasks as part of overall totals
        const extraDoneCount = isExtraTask ? (uniquePendingCount + uniqueConfirmedCount) : 0;

        const taskDoneUnique = uniquePendingCount; // pending considered "done" for local progress
        const taskConfirmedUnique = uniqueConfirmedCount;

        return {
          ...task,
          doneCount: taskDoneUnique,
          totalSubTasks,
          confirmedCount: taskConfirmedUnique,
          confirmedNoNotesCount,
          extraDoneCount,
          percent: totalSubTasks > 0 ? ((taskDoneUnique + taskConfirmedUnique) / totalSubTasks) * 100 : 0,
          reports: pendingReports,
          shift_name: shiftData.shift
        };
      });

      // Total calculation for shift (include extra tasks in totals)
      const totalDoneUnique = shiftTasks.reduce((acc, t) => acc + (t.doneCount || 0), 0);
      const totalConfirmedUnique = shiftTasks.reduce((acc, t) => acc + (t.confirmedCount || 0), 0);
      const totalConfirmedNoNotes = shiftTasks.reduce((acc, t) => acc + (t.confirmedNoNotesCount || 0), 0);
      const totalExtraDone = shiftTasks.reduce((acc, t) => acc + (t.extraDoneCount || 0), 0);
      const totalPossible = shiftTasks.reduce((acc, t) => acc + (t.totalSubTasks || 0), 0);

      const totalCount = totalDoneUnique + totalConfirmedUnique;
      const rawPercent = totalPossible > 0 ? (totalCount / totalPossible) * 100 : 0;
      const cappedPercent = Math.min(100, rawPercent);

      return {
        ...shiftData,
        tasks: shiftTasks,
        totalPercent: cappedPercent,
        totalDone: totalDoneUnique,
        totalConfirmed: totalConfirmedUnique,
        totalConfirmedNoNotes,
        totalExtraTasks: totalExtraDone,
        totalPossible
      };
    });
  }, [reports, mergedTasks]);

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Helper untuk mencatat indeks subtask yang sudah dikonfirmasi hari ini
  const addConfirmedIndex = (taskId, index) => {
    const today = new Date().toISOString().split('T')[0];
    const key = `ob_confirmed_indexes_${today}`;
    try {
      const data = JSON.parse(localStorage.getItem(key) || '{}');
      if (!data[taskId]) data[taskId] = [];
      if (!data[taskId].includes(index)) {
        data[taskId].push(index);
        localStorage.setItem(key, JSON.stringify(data));
        setExtraTasksRefresh(prev => prev + 1); // trigger re-render
      }
    } catch (e) { console.error(e); }
  };

  const getConfirmedIndexes = (taskId) => {
    const today = new Date().toISOString().split('T')[0];
    const key = `ob_confirmed_indexes_${today}`;
    try {
      const data = JSON.parse(localStorage.getItem(key) || '{}');
      return data[taskId] || [];
    } catch (e) { return []; }
  };

  const getConfirmedCount = (taskId) => {
    return getConfirmedIndexes(taskId).length;
  };

  const isTaskConfirmedToday = (taskId, totalSubTasks) => {
    if (totalSubTasks === 0) return false;
    return getConfirmedCount(taskId) >= totalSubTasks;
  };

  const handleAddSubTask = async (taskId) => {
    const list = JSON.parse(localStorage.getItem('karyawan_pengganti_list') || '[]');
    // Filter khusus jabatan Office Boy
    const obList = list.filter(emp => emp.jabatan && emp.jabatan.toLowerCase() === 'office boy');

    const { value: formValues } = await Swal.fire({
      title: 'Tambah Sub Tugas',
      html: `
        <div style="text-align: left; margin-bottom: 12px;">
          <label style="font-size: 14px; font-weight: 600; color: #475569;">Pilih Petugas OB</label>
          <div style="margin-top: 8px;">
            <select id="swal-input-user" style="width: 100%;"></select>
          </div>
        </div>
        <div style="text-align: left;">
          <label style="font-size: 14px; font-weight: 600; color: #475569;">Nama Sub Tugas Tambahan</label>
          <input id="swal-input-task" class="swal2-input" placeholder="Contoh: Bersihkan AC Ruang Meeting" style="width: 100%; max-width: 100%; margin: 8px 0; box-sizing: border-box; border-radius: 8px;">
        </div>
      `,
      didOpen: () => {
        new TomSelect('#swal-input-user', {
          valueField: "id",
          labelField: "nama",
          searchField: "nama",
          placeholder: "Cari nama petugas OB...",
          options: obList,
          render: {
            option: (data, escape) => `
              <div style="padding: 10px 12px; border-bottom: 1px solid #f1f5f9;">
                <div style="font-weight: 600; color: #1e293b;">${escape(data.nama)}</div>
                <div style="font-size: 11px; color: #64748b;">${escape(data.kantor || 'Cabang')}</div>
              </div>
            `,
            item: (data, escape) => `
              <div style="font-weight: 500;">${escape(data.nama)}</div>
            `,
          }
        });
      },
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonColor: '#2563eb',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        const userId = document.getElementById('swal-input-user').value;
        const taskName = document.getElementById('swal-input-task').value;

        if (!userId || !taskName) {
          Swal.showValidationMessage('Petugas dan Nama Tugas harus diisi');
          return false;
        }

        try {
          const res = await fetch('https://jne-cilacap.com/slip/public/api/ob/extra-task', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              task_id: taskId,
              item_name: taskName,
              user_id: userId
            })
          });
          return await res.json();
        } catch (e) {
          Swal.showValidationMessage(`Request failed: ${e}`);
        }
      },
      allowOutsideClick: () => !Swal.isLoading()
    });

    if (formValues && formValues.status === 'success') {
      fetchExtraTasks();
      Swal.fire({
        icon: 'success',
        title: 'Berhasil',
        text: 'Sub tugas ditambahkan ke server',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    }
  };

  // Buka panel konfirmasi per-item (catatan + TTD)
  const openConfirmPanel = (report) => {
    setConfirmPanel({ report });
  };

  const handleApprove = async (reportId, catatan = '', ttdBase64 = '') => {
    const report = reports.find(r => r.id === reportId);
    try {
      const formData = new FormData();
      formData.append('catatan', catatan);
      if (ttdBase64) formData.append('ttd_konfirmasi', ttdBase64);

      const res = await fetch(`https://jne-cilacap.com/slip/public/api/ob/approve/${reportId}`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      });
      const json = await res.json();

      if (json.status === 'success') {
        // Tambahkan index konfirmasi
        const taskId = report?.task_id;
        const subIndex = report?.subtask_index;
        if (taskId && subIndex !== undefined) {
          addConfirmedIndex(taskId, subIndex);
        }

        setReports(prev => prev.filter(r => r.id !== reportId));
        setSelectedIds(prev => prev.filter(id => id !== reportId));
        if (selectedTask) {
          setSelectedTask(prev => ({
            ...prev,
            reports: prev.reports.filter(r => r.id !== reportId),
            doneCount: prev.doneCount - 1
          }));
        }

        Swal.fire({
          icon: 'success',
          title: 'Berhasil',
          text: 'Laporan dikonfirmasi.',
          timer: 1000,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      } else {
        Swal.fire('Gagal', json.message || 'Konfirmasi gagal', 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'Gagal memproses konfirmasi', 'error');
    }
  };

  const submitConfirmPanel = async () => {
    if (!confirmPanel) return;
    const reportId = confirmPanel.report.id;
    const ttdBase64 = canvasRef.current ? canvasRef.current.toDataURL('image/png') : '';
    setConfirmPanel(null);
    await handleApprove(reportId, catatanKonfirmasi, ttdBase64);
  };

  const handleBulkApprove = async () => {
    const itemsToApprove = selectedTask.reports.filter(r => selectedIds.includes(r.id));
    if (itemsToApprove.length === 0) return;

    const result = await Swal.fire({
      title: `Konfirmasi ${itemsToApprove.length} Laporan?`,
      text: "Semua foto terpilih akan dihapus dari server guna menghemat storage.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#22c55e',
      confirmButtonText: 'Ya, Konfirmasi Semua',
      cancelButtonColor: '#f8fafc',
      cancelButtonText: 'Batal'
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: 'Memproses...',
        html: 'Mohon tunggu sebentar',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Proses satu per satu dengan delay kecil agar server tidak overload
      for (const report of itemsToApprove) {
        try {
          const res = await fetch(`https://jne-cilacap.com/slip/public/api/ob/approve/${report.id}`, {
            method: 'POST'
          });
          if (res.ok) {
            // Update state lokal
            setReports(prev => prev.filter(r => r.id !== report.id));
            setSelectedIds(prev => prev.filter(id => id !== report.id));
          }
          await new Promise(resolve => setTimeout(resolve, 200));
        } catch (e) {
          console.error('Bulk approval error:', e);
        }
      }

      // Tambahkan index konfirmasi untuk task ini
      for (const report of itemsToApprove) {
        if (report.task_id && report.subtask_index !== undefined) {
          addConfirmedIndex(report.task_id, report.subtask_index);
        }
      }

      // Update selectedTask agar modal update
      setSelectedTask(prev => ({
        ...prev,
        reports: prev.reports.filter(r => !selectedIds.includes(r.id)),
        doneCount: prev.doneCount - itemsToApprove.length
      }));

      Swal.fire({
        icon: 'success',
        title: 'Selesai',
        text: `${itemsToApprove.length} Laporan berhasil dikonfirmasi.`,
        timer: 2000
      });
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <div className="animate-spin" style={{ width: 30, height: 30, border: '3px solid #2563eb', borderTopColor: 'transparent', borderRadius: '50%' }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 40 }}>
      {/* Dashboard Header */}
      <div style={{ padding: '0 16px', marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#2563eb', marginBottom: 4, letterSpacing: 1 }}>MONITORING OFFICE BOY</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 24, fontWeight: 900, color: '#1e293b', margin: 0 }}>
            Halo, {JSON.parse(localStorage.getItem('data_karyawan') || '{}').nama?.split(' ')[0] || 'Admin'} 👋
          </h2>
          <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>
            {new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>
      </div>

      {shiftProgress.map((shift, sIdx) => (
        <motion.div
          key={sIdx}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: sIdx * 0.1 }}
          style={{
            backgroundColor: "#fff",
            borderRadius: 24,
            padding: "24px 20px",
            width: "100%",
            maxWidth: 450,
            margin: "0 auto 24px auto",
            boxShadow: "0 4px 15px rgba(0, 0, 0, 0.05)",
            border: "1px solid #cbd5e1",
            boxSizing: 'border-box'
          }}
        >
          {/* Shift Header with Blue Card Design */}
          <div style={{ backgroundColor: '#2563eb', borderRadius: 20, padding: '20px', marginBottom: 20, color: '#fff' }}>
            {/* Header: Shift name + Percent */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.9, marginBottom: 2 }}>Shift Awal</div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>
                  {shift.shift.split(' – ')[0]} - {shift.shift.split(' – ')[1]}
                </div>
              </div>
              <div style={{
                backgroundColor: '#22c55e',
                color: '#166534',
                padding: '8px 16px',
                borderRadius: 12,
                fontWeight: 800,
                fontSize: 18
              }}>
                {Math.round(shift.totalPercent)}%
              </div>
            </div>

            {/* Stat boxes */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              {/* Diserahkan */}
              <div style={{
                flex: 1,
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderRadius: 12,
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>✓</div>
                <div style={{ fontSize: 11, opacity: 0.85, marginBottom: 4 }}>Diserahkan</div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>{shift.totalDone}</div>
              </div>

              {/* Menunggu */}
              <div style={{
                flex: 1,
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderRadius: 12,
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>🕒</div>
                <div style={{ fontSize: 11, opacity: 0.85, marginBottom: 4 }}>Menunggu</div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>{shift.totalConfirmedNoNotes}</div>
              </div>

              {/* Tindakan */}
              <div style={{
                flex: 1,
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderRadius: 12,
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>⚠️</div>
                <div style={{ fontSize: 11, opacity: 0.85, marginBottom: 4 }}>Tindakan</div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>{shift.totalExtraTasks}</div>
              </div>
            </div>

            {/* Footer: Personnel Info */}
            {shift.tasks.some(t => t.reports.length > 0) && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                borderTop: '1px solid rgba(255,255,255,0.2)',
                paddingTop: 12
              }}>
                <div style={{ fontSize: 11, opacity: 0.9 }}>Penyergapan:</div>
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  {[...new Set(shift.tasks.flatMap(t => t.reports.map(r => r.display_name)))].filter(Boolean).slice(0, 2).map((name, idx) => (
                    <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 6, height: 6, backgroundColor: '#22c55e', borderRadius: '50%' }} />
                      {name}
                      {idx < [...new Set(shift.tasks.flatMap(t => t.reports.map(r => r.display_name)))].filter(Boolean).slice(0, 2).length - 1 && ', '}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {shift.tasks.map((task) => (
              <motion.div
                key={task.id}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  if (task.reports.length > 0) {
                    setSelectedTask(task);
                    setIsModalOpen(true);
                  } else {
                    Swal.fire({
                      text: 'Belum ada laporan foto untuk kategori ini',
                      icon: 'info',
                      toast: true,
                      position: 'top-end',
                      timer: 2000,
                      showConfirmButton: false
                    });
                  }
                }}
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: 20,
                  padding: '16px',
                  border: task.reports.length > 0
                    ? '1px solid #10b981' // Hijau jika sudah dikerjakan (menunggu konfirmasi)
                    : isTaskConfirmedToday(task.id)
                      ? '1px solid #3b82f6' // Biru jika sudah dikonfirmasi
                      : '1px solid #f59e0b', // Kuning jika belum dikerjakan
                  boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: task.reports.length > 0 ? 'pointer' : 'default'
                }}
              >
                {/* Micro Progress Bar */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, height: 4, width: '100%', backgroundColor: '#f1f5f9' }} />
                <motion.div
                  initial={{ width: 0 }}
                  animate={{
                    width: task.totalSubTasks > 0
                      ? `${((task.doneCount + getConfirmedCount(task.id)) / task.totalSubTasks) * 100}%`
                      : '0%'
                  }}
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: 4,
                    backgroundColor: isTaskConfirmedToday(task.id, task.totalSubTasks)
                      ? '#3b82f6'
                      : task.reports.length > 0 || getConfirmedCount(task.id) > 0 ? '#10b981' : '#f59e0b'
                  }}
                />

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                    <div style={{ position: 'relative' }}>
                      <div style={{
                        width: 42, height: 42, borderRadius: 12,
                        backgroundColor: isTaskConfirmedToday(task.id, task.totalSubTasks)
                          ? '#eff6ff'
                          : task.reports.length > 0 || getConfirmedCount(task.id) > 0 ? '#dcfce7' : '#fef3c7',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20
                      }}>
                        {isTaskConfirmedToday(task.id, task.totalSubTasks)
                          ? '✅'
                          : (task.reports.length > 0 || getConfirmedCount(task.id) > 0 ? '🕒' : (task.id === 1 ? '🧹' : task.id === 2 ? '🏢' : '🌳'))}
                      </div>
                      {task.reports.length > 0 && (
                        <div style={{
                          position: 'absolute', top: -6, right: -6,
                          backgroundColor: '#ef4444', color: '#fff',
                          borderRadius: 999, fontSize: 10, fontWeight: 800,
                          padding: '2px 5px', minWidth: 16, textAlign: 'center',
                          border: '2px solid #fff', lineHeight: 1.4,
                          boxShadow: '0 2px 6px rgba(239,68,68,0.5)'
                        }}>
                          {task.reports.length}
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                        {task.title.length > 30 ? task.title.substring(0, 30) + '...' : task.title}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
                        {isTaskConfirmedToday(task.id, task.totalSubTasks) ? (
                          <span style={{ fontWeight: 800, color: '#3b82f6' }}>{task.totalSubTasks} / {task.totalSubTasks} SUDAH DIKONFIRMASI</span>
                        ) : task.reports.length > 0 || getConfirmedCount(task.id) > 0 ? (
                          <>
                            {getConfirmedCount(task.id) > 0 && (
                              <>
                                <span style={{ fontWeight: 600, color: '#3b82f6' }}>
                                  {getConfirmedCount(task.id)} / {task.totalSubTasks} Dikonfirmasi
                                </span>
                                {task.reports.length > 0 && <span style={{ color: '#cbd5e1' }}>•</span>}
                              </>
                            )}
                            {task.reports.length > 0 && (
                              <>
                                <span style={{ fontWeight: 600, color: '#10b981' }}>
                                  {task.doneCount} Menunggu Konfirmasi
                                </span>
                                <span style={{ color: '#cbd5e1' }}>•</span>
                                <span style={{ color: '#64748b' }}>
                                  Oleh: {[...new Set(task.reports.map(r => r.display_name))].join(', ')}
                                </span>
                              </>
                            )}
                          </>
                        ) : (
                          <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                            {task.totalSubTasks === 0 ? 'BELUM ADA TUGAS' : 'BELUM DIKERJAKAN'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {(task.id === 10 || task.id === 11) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddSubTask(task.id);
                        }}
                        style={{
                          padding: '6px 10px',
                          backgroundColor: '#eff6ff',
                          color: '#2563eb',
                          border: '1px solid #dbeafe',
                          borderRadius: 8,
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        + Tambah
                      </button>
                    )}
                    {task.reports.length > 0 && <ChevronRight size={18} color="#cbd5e1" />}
                  </div>
                </div>
                {/* Show History for Tugas Tambahan */}
                {(task.id === 10 || task.id === 11) && task.subTasks.length > 0 && (
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px dashed #e2e8f0' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>
                      Riwayat Tugas Terkirim ({task.subTasks.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {task.subTasks.map((st) => {
                        const subTaskId = st.id;
                        const isConfirmed = getConfirmedIndexes(task.id).includes(subTaskId);
                        const isDone = task.reports.some(r => parseInt(r.subtask_index) === parseInt(subTaskId));

                        let statusColor = '#f59e0b'; // Kuning (Terkirim)
                        if (isConfirmed) statusColor = '#3b82f6'; // Biru (Sudah dikonfirmasi)
                        else if (isDone) statusColor = '#10b981'; // Hijau (Sudah dikerjakan)

                        return (
                          <div key={subTaskId} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                            <div style={{ color: statusColor, marginTop: 2, fontSize: 14 }}>✓</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>{st.title}</div>
                              {st.added_by_name && (
                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
                                  <span>👤</span>
                                  <span>Ditambahkan oleh: <strong style={{ color: '#64748b' }}>{st.added_by_name}</strong></span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      ))}

      {/* MODAL REVIEW DETAIL */}
      <AnimatePresence>
        {isModalOpen && selectedTask && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              style={{
                width: '100%',
                maxWidth: 500,
                backgroundColor: '#fff',
                borderTopLeftRadius: 32,
                borderTopRightRadius: 32,
                padding: '24px',
                maxHeight: '90vh',
                overflowY: 'auto',
                position: 'relative',
                zIndex: 1001,
                boxShadow: '0 -10px 40px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ width: 40, height: 4, backgroundColor: '#e2e8f0', borderRadius: 2, margin: '0 auto 20px auto' }} />

              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', marginBottom: 4 }}>REVIEW LAPORAN</div>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', margin: 0 }}>{selectedTask.title}</h3>
                <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0 0' }}>Shift {selectedTask.shift_name}</p>
              </div>

              {/* Bulk Action Header */}
              {selectedTask.reports.filter(r => r.status !== 'Dikonfirmasi').length > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 20,
                  padding: '12px 16px',
                  backgroundColor: '#f8fafc',
                  borderRadius: 16,
                  border: '1px solid #e2e8f0'
                }}>
                  <div
                    onClick={() => {
                      const pendingReports = selectedTask.reports.filter(r => r.status !== 'Dikonfirmasi');
                      const allIdsInCurrentTask = pendingReports.map(r => r.id);
                      const isAllSelected = allIdsInCurrentTask.every(id => selectedIds.includes(id));

                      if (isAllSelected) {
                        setSelectedIds(prev => prev.filter(id => !allIdsInCurrentTask.includes(id)));
                      } else {
                        setSelectedIds(prev => [...new Set([...prev, ...allIdsInCurrentTask])]);
                      }
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                  >
                    <div style={{
                      width: 22, height: 22, borderRadius: 6,
                      border: `2px solid ${selectedTask.reports.filter(r => r.status !== 'Dikonfirmasi').every(r => selectedIds.includes(r.id)) ? '#2563eb' : '#cbd5e1'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: selectedTask.reports.filter(r => r.status !== 'Dikonfirmasi').every(r => selectedIds.includes(r.id)) ? '#2563eb' : '#fff',
                      transition: 'all 0.2s'
                    }}>
                      {selectedTask.reports.filter(r => r.status !== 'Dikonfirmasi').every(r => selectedIds.includes(r.id)) && <CheckCircle size={14} color="#fff" strokeWidth={3} />}
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#475569' }}>Pilih Semua</span>
                  </div>

                  {selectedTask.reports.filter(r => r.status !== 'Dikonfirmasi').some(r => selectedIds.includes(r.id)) && (
                    <motion.button
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      onClick={handleBulkApprove}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#22c55e',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 12,
                        fontSize: 13,
                        fontWeight: 800,
                        boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
                      }}
                    >
                      Konfirmasi ({selectedTask.reports.filter(r => r.status !== 'Dikonfirmasi' && selectedIds.includes(r.id)).length})
                    </motion.button>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {selectedTask.reports.filter(report => report.status !== 'Dikonfirmasi').map((report) => (
                  <div key={report.id} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div
                        onClick={() => toggleSelect(report.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                      >
                        <div style={{
                          width: 22, height: 22, borderRadius: 6,
                          border: `2px solid ${selectedIds.includes(report.id) ? '#2563eb' : '#cbd5e1'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: selectedIds.includes(report.id) ? '#2563eb' : '#fff',
                          transition: 'all 0.2s'
                        }}>
                          {selectedIds.includes(report.id) && <CheckCircle size={14} color="#fff" strokeWidth={3} />}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <User size={16} color="#22c55e" />
                          </div>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>{report.display_name}</span>
                        </div>
                      </div>
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(report.updated_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>

                    <p style={{ fontSize: 13, color: '#475569', marginBottom: 10, fontWeight: 500 }}>
                      Item: {selectedTask.subTasks.find(st => parseInt(st.id) === parseInt(report.subtask_index))?.title || 'Tugas Tambahan'}
                    </p>

                    <div style={{
                      position: 'relative',
                      borderRadius: 16,
                      overflow: 'hidden',
                      height: 280,
                      marginBottom: 8,
                      backgroundColor: '#f1f5f9'
                    }}>
                      <img
                        src={report.photo_path}
                        alt="Bukti"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          opacity: 0,
                          transition: 'opacity 0.3s ease'
                        }}
                        onLoad={(e) => e.target.style.opacity = 1}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/400x300?text=Foto+Tidak+Ditemukan';
                          e.target.style.opacity = 1;
                        }}
                      />
                    </div>

                    {!selectedIds.includes(report.id) && (
                      <button
                        onClick={() => openConfirmPanel(report)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 12,
                          fontSize: 13,
                          fontWeight: 800,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6
                        }}
                      >
                        <PenLine size={14} />
                        Konfirmasi + Catatan & TTD
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={() => setIsModalOpen(false)}
                style={{ width: '100%', padding: '16px', marginTop: 10, backgroundColor: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 16, fontWeight: 700, cursor: 'pointer' }}
              >
                Tutup
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* PANEL KONFIRMASI DENGAN CATATAN + TTD */}
      <AnimatePresence>
        {confirmPanel && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setConfirmPanel(null)}
              style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 220 }}
              style={{
                width: '100%',
                maxWidth: 500,
                backgroundColor: '#fff',
                borderTopLeftRadius: 32,
                borderTopRightRadius: 32,
                padding: '24px 20px 36px',
                maxHeight: '90vh',
                overflowY: 'auto',
                position: 'relative',
                zIndex: 2001,
                boxShadow: '0 -12px 50px rgba(0,0,0,0.15)'
              }}
            >
              {/* Handle bar */}
              <div style={{ width: 40, height: 4, backgroundColor: '#e2e8f0', borderRadius: 2, margin: '0 auto 20px auto' }} />

              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#22c55e', letterSpacing: 1, marginBottom: 4 }}>KONFIRMASI LAPORAN</div>
                  <h3 style={{ fontSize: 17, fontWeight: 800, color: '#1e293b', margin: 0 }}>
                    {selectedTask?.subTasks?.find(st => parseInt(st.id) === parseInt(confirmPanel.report.subtask_index))?.title || 'Sub Tugas'}
                  </h3>
                  <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>Oleh: {confirmPanel.report.display_name}</p>
                </div>
                <button
                  onClick={() => setConfirmPanel(null)}
                  style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                  <X size={16} color="#64748b" />
                </button>
              </div>

              {/* Preview foto kecil */}
              {confirmPanel.report.photo_path && (
                <div style={{ borderRadius: 14, overflow: 'hidden', height: 140, marginBottom: 20, backgroundColor: '#f1f5f9' }}>
                  <img
                    src={confirmPanel.report.photo_path}
                    alt="Bukti"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Foto'; }}
                  />
                </div>
              )}

              {/* Input Catatan */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                  📝 Catatan Konfirmasi
                </label>
                <textarea
                  value={catatanKonfirmasi}
                  onChange={(e) => setCatatanKonfirmasi(e.target.value)}
                  placeholder="Tuliskan catatan atau keterangan tambahan... (opsional)"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: '1.5px solid #e2e8f0',
                    fontSize: 14,
                    color: '#1e293b',
                    resize: 'vertical',
                    outline: 'none',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    backgroundColor: '#f8fafc',
                    lineHeight: 1.5
                  }}
                />
              </div>

              {/* Canvas TTD */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>✍️ Tanda Tangan Admin</label>
                  <button
                    onClick={clearCanvas}
                    style={{ fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Hapus TTD
                  </button>
                </div>
                <canvas
                  ref={canvasRef}
                  width={300}
                  height={150}
                  style={{
                    width: '100%',
                    height: 150,
                    border: '1.5px dashed #94a3b8',
                    borderRadius: 12,
                    backgroundColor: '#f8fafc',
                    touchAction: 'none',
                    display: 'block',
                    cursor: 'crosshair'
                  }}
                  onMouseDown={(e) => startDraw(e)}
                  onMouseMove={(e) => onDraw(e)}
                  onMouseUp={stopDraw}
                  onMouseLeave={stopDraw}
                  onTouchStart={(e) => { e.preventDefault(); startDraw(e, true); }}
                  onTouchMove={(e) => { e.preventDefault(); onDraw(e, true); }}
                  onTouchEnd={stopDraw}
                />
                <p style={{ fontSize: 11, color: '#94a3b8', margin: '6px 0 0', textAlign: 'center' }}>Gambar tanda tangan di area di atas</p>
              </div>

              {/* Tombol aksi */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setConfirmPanel(null)}
                  style={{
                    flex: 1,
                    padding: '14px',
                    backgroundColor: '#f1f5f9',
                    color: '#64748b',
                    border: 'none',
                    borderRadius: 14,
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: 'pointer'
                  }}
                >
                  Batal
                </button>
                <button
                  onClick={submitConfirmPanel}
                  style={{
                    flex: 2,
                    padding: '14px',
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 14,
                    fontWeight: 800,
                    fontSize: 14,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    boxShadow: '0 4px 16px rgba(34,197,94,0.35)'
                  }}
                >
                  <CheckCircle size={16} />
                  Konfirmasi Sekarang
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ObConfirmationList;
